import {
  buildInitialProposalData,
  createVenue,
  normalizeProposalData,
} from '@/utils/proposalBuilder';
import { getProposalDemandDefaults, PROPOSAL_STORAGE_KEY } from '@/constants/proposalBuilder';
import * as api from '@/services/proposalBuilder.service';

function readStoredProposal() {
  if (typeof localStorage === 'undefined') return {};

  try {
    const saved = JSON.parse(localStorage.getItem(PROPOSAL_STORAGE_KEY));
    if (!saved || typeof saved !== 'object') return {};
    // Older drafts stored only the form fields.
    const proposal = saved.data ? saved : { data: saved };
    return { ...proposal, data: normalizeProposalData(proposal.data) };
  } catch {
    return {};
  }
}

function defaultState() {
  const saved = readStoredProposal();
  return {
    id: typeof saved.id === 'string' ? saved.id : null,
    clientId: typeof saved.clientId === 'string' ? saved.clientId : null,
    data: saved.data || buildInitialProposalData(),
    loading: false,
    error: '',
    lastSavedAt: null,
  };
}

export default {
  namespaced: true,
  state: defaultState,
  getters: {
    data: (state) => state.data,
    id: (state) => state.id,
    clientId: (state) => state.clientId,
    plan: (state) => getProposalDemandDefaults(state.data.plan),
    venues: (state) => state.data.venues,
    loading: (state) => state.loading,
    error: (state) => state.error,
    lastSavedAt: (state) => state.lastSavedAt,
    isDirty: (state) => state.id !== null,
  },
  mutations: {
    RESET_STATE(state) {
      Object.assign(state, defaultState());
    },
    SET_ID(state, id) {
      state.id = id;
    },
    SET_CLIENT_ID(state, clientId) {
      state.clientId = clientId;
    },
    SET_DATA(state, data) {
      state.data = normalizeProposalData(data);
    },
    SET_FIELD(state, { key, value }) {
      state.data = { ...state.data, [key]: value };
    },
    SET_VENUES(state, venues) {
      state.data = { ...state.data, venues };
    },
    UPDATE_VENUE(state, { index, key, value }) {
      state.data = {
        ...state.data,
        venues: state.data.venues.map((venue, venueIndex) => (
          venueIndex === index ? { ...venue, [key]: value } : venue
        )),
      };
    },
    ADD_VENUE(state) {
      const plan = getProposalDemandDefaults(state.data.plan);
      const venue = createVenue(state.data.venues.length, plan.waiters, plan.training);
      state.data = { ...state.data, venues: [...state.data.venues, venue] };
    },
    REMOVE_VENUE(state, index) {
      if (state.data.venues.length <= 1) return;
      state.data = {
        ...state.data,
        venues: state.data.venues.filter((_, venueIndex) => venueIndex !== index),
      };
    },
    RESIZE_VENUES(state, count) {
      const target = Math.max(1, count);
      const plan = getProposalDemandDefaults(state.data.plan);
      const next = [...state.data.venues];

      while (next.length < target) {
        next.push(createVenue(next.length, plan.waiters, plan.training));
      }

      state.data = { ...state.data, venues: next.slice(0, target) };
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_LAST_SAVED_AT(state, timestamp) {
      state.lastSavedAt = timestamp;
    },
    PERSIST(state) {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(PROPOSAL_STORAGE_KEY, JSON.stringify({
        id: state.id, clientId: state.clientId, data: state.data,
      }));
    },
  },
  actions: {
    resetState({ commit }) {
      commit('RESET_STATE');
    },
    setData({ commit }, data) {
      commit('SET_DATA', data);
    },
    setField({ commit }, payload) {
      commit('SET_FIELD', payload);
    },
    setVenues({ commit }, venues) {
      commit('SET_VENUES', venues);
    },
    updateVenue({ commit }, payload) {
      commit('UPDATE_VENUE', payload);
    },
    addVenue({ commit }) {
      commit('ADD_VENUE');
    },
    removeVenue({ commit }, index) {
      commit('REMOVE_VENUE', index);
    },
    resizeVenues({ commit }, count) {
      commit('RESIZE_VENUES', count);
    },
    persist({ commit }) {
      commit('PERSIST');
    },

    async saveToServer({ state, commit }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', '');
      try {
        if (!state.clientId) throw new Error('Откройте КП из карточки клиента');
        const payload = {
          ...state.data,
          clientId: state.clientId,
        };

        let result;
        if (state.id) {
          result = await api.updateProposal(state.id, payload);
        } else {
          result = await api.createProposal(payload);
        }

        const saved = result?.data || result;
        if (!saved?.id || saved.clientId !== state.clientId) {
          throw new Error('CRM не подтвердила сохранение КП');
        }
        if (saved?.id) commit('SET_ID', saved.id);
        if (saved?.clientId) commit('SET_CLIENT_ID', saved.clientId);
        commit('SET_LAST_SAVED_AT', new Date().toISOString());
        commit('PERSIST');
        return saved;
      } catch (requestError) {
        const message = requestError?.response?.data?.message
          || requestError?.message || 'Не удалось сохранить КП';
        commit('SET_ERROR', message);
        throw requestError;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async loadFromServer({ commit }, id) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', '');
      try {
        const result = await api.getProposal(id);
        const proposal = result?.data || result;
        if (proposal) {
          commit('SET_ID', proposal.id);
          commit('SET_CLIENT_ID', proposal.clientId || null);
          commit('SET_DATA', {
            company: proposal.company,
            contact: proposal.contact,
            plan: proposal.plan,
            venues: proposal.venues,
            annual: proposal.annual,
            tips: proposal.tips,
            validUntil: proposal.validUntil,
            manager: proposal.manager,
          });
          commit('SET_LAST_SAVED_AT', proposal.updatedAt || proposal.createdAt || null);
          commit('PERSIST');
        }
        return proposal;
      } catch (requestError) {
        const message = requestError?.response?.data?.message
          || (typeof requestError === 'string' ? requestError : 'Не удалось загрузить КП');
        commit('SET_ERROR', message);
        throw requestError;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async loadByClientId({ commit }, clientId) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', '');
      try {
        const result = await api.listProposals({ clientId });
        const list = result?.data || result;
        const proposals = Array.isArray(list) ? list : list?.items || [];
        if (proposals.length > 0) {
          const proposal = proposals[0];
          commit('SET_ID', proposal.id);
          commit('SET_CLIENT_ID', proposal.clientId || clientId);
          commit('SET_DATA', {
            company: proposal.company,
            contact: proposal.contact,
            plan: proposal.plan,
            venues: proposal.venues,
            annual: proposal.annual,
            tips: proposal.tips,
            validUntil: proposal.validUntil,
            manager: proposal.manager,
          });
          commit('SET_LAST_SAVED_AT', proposal.updatedAt || proposal.createdAt || null);
          commit('PERSIST');
          return proposal;
        }
        commit('SET_CLIENT_ID', clientId);
        return null;
      } catch (requestError) {
        const message = requestError?.response?.data?.message
          || (typeof requestError === 'string' ? requestError : 'Не удалось загрузить КП');
        commit('SET_ERROR', message);
        throw requestError;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteFromServer({ state, commit }) {
      if (!state.id) return;
      commit('SET_LOADING', true);
      commit('SET_ERROR', '');
      try {
        await api.deleteProposal(state.id);
        commit('SET_ID', null);
        commit('SET_CLIENT_ID', null);
        commit('SET_LAST_SAVED_AT', null);
        commit('PERSIST');
      } catch (requestError) {
        const message = requestError?.response?.data?.message
          || (typeof requestError === 'string' ? requestError : 'Не удалось удалить КП');
        commit('SET_ERROR', message);
        throw requestError;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    newProposal({ commit }, { clientId, company } = {}) {
      commit('RESET_STATE');
      commit('SET_ID', null);
      commit('SET_CLIENT_ID', null);
      if (clientId) commit('SET_CLIENT_ID', clientId);
      commit('SET_DATA', {
        ...buildInitialProposalData(),
        ...(company ? { company } : {}),
      });
      commit('PERSIST');
    },
  },
};
